<template>
  <section class="discounted-cases" :aria-labelledby="headingId">
    <div class="discounted-cases__backdrop" aria-hidden="true">
      <span class="discounted-cases__beam discounted-cases__beam--left"></span>
      <span class="discounted-cases__beam discounted-cases__beam--right"></span>
      <span class="discounted-cases__beam discounted-cases__beam--center"></span>
      <span class="discounted-cases__headline-shadow">0%</span>
      <span class="discounted-cases__streaks"></span>
      <span class="discounted-cases__grid"></span>
      <span class="discounted-cases__glow"></span>
      <span class="discounted-cases__noise"></span>
    </div>

    <div class="discounted-cases__header">
      <p class="discounted-cases__eyebrow">{{ t('CASES_PAGE.discountedShowcase.eyebrow') }}</p>
      <h2 :id="headingId">{{ t('CASES_PAGE.discountedShowcase.title') }}</h2>
      <p class="discounted-cases__description">
        {{ t('CASES_PAGE.discountedShowcase.description') }}
      </p>

      <div class="discounted-cases__proof">
        <div class="discounted-cases__proof-item">
          <strong>6</strong>
          <span>{{ t('CASES_PAGE.discountedShowcase.dailyCases') }}</span>
        </div>
        <div class="discounted-cases__proof-item discounted-cases__proof-item--edge">
          <strong>0%</strong>
          <span>{{ t('CASES_PAGE.discountedShowcase.houseEdge') }}</span>
        </div>
      </div>
    </div>

    <div class="discounted-cases__list">
      <router-link
        v-for="caseItem in cases"
        :key="caseItem.id"
        class="discounted-cases__link"
        :to="{ name: 'case-detail', params: { caseName: encodeCaseName(caseItem.name) } }"
      >
        <SingleCase :caseData="caseItem" :showWpIconOnMainPage="caseItem.isEventFeatured" />
      </router-link>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useId } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Case } from '@/services/models/models'
import { encodeCaseName } from '@/utils/caseNameUrl'
import SingleCase from './SingleCase.vue'

interface Props {
  cases: Case[]
}

defineProps<Props>()

const { t } = useI18n()
const headingId = `discounted-cases-${useId()}`
</script>

<style scoped lang="scss">
.discounted-cases {
  position: relative;
  display: grid;
  grid-template-columns: minmax(220px, 0.75fr) minmax(0, 2.25fr);
  align-items: center;
  min-height: 430px;
  padding: clamp(1.25rem, 3vw, 2.5rem);
  border-radius: $radius-lg;
  overflow: hidden;
  isolation: isolate;
  background:
    linear-gradient(112deg, rgba(200, 242, 79, 0.08) 0 9%, transparent 9% 16%),
    linear-gradient(128deg, transparent 0 42%, rgba(200, 242, 79, 0.2) 42% 52%, transparent 52%),
    linear-gradient(135deg, #06111d 0%, #092032 42%, #071018 100%);
  border: 1px solid rgba(200, 242, 79, 0.55);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.06),
    inset 0 0 54px rgba(200, 242, 79, 0.1),
    0 22px 60px rgba(0, 0, 0, 0.3);

  &::after {
    content: '';
    position: absolute;
    inset: -45% -18%;
    z-index: 0;
    background: linear-gradient(
      104deg,
      transparent 38%,
      rgba(255, 255, 255, 0.18) 47%,
      rgba(200, 242, 79, 0.24) 50%,
      transparent 60%
    );
    transform: translateX(-62%) skewX(-14deg);
    animation: discounted-cases-sweep 6s ease-in-out infinite;
    pointer-events: none;
  }

  @include tablet {
    grid-template-columns: 1fr;
    gap: $space-lg;
    min-height: auto;
  }

  @include mobile {
    padding: $space-md;
  }
}

.discounted-cases__backdrop,
.discounted-cases__beam,
.discounted-cases__headline-shadow,
.discounted-cases__streaks,
.discounted-cases__grid,
.discounted-cases__glow,
.discounted-cases__noise {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.discounted-cases__backdrop {
  z-index: -1;
}

.discounted-cases__beam {
  background: linear-gradient(
    135deg,
    rgba(200, 242, 79, 0.42),
    rgba(244, 210, 74, 0.2) 58%,
    rgba(75, 104, 253, 0.18)
  );
  clip-path: polygon(14% 0, 100% 0, 86% 100%, 0 100%);
  opacity: 0.92;

  &--left {
    left: -3%;
    width: 34%;
    transform: skewX(-16deg);
  }

  &--right {
    right: -2%;
    width: 36%;
    transform: skewX(16deg);
  }

  &--center {
    left: 33%;
    width: 30%;
    background: linear-gradient(180deg, rgba(244, 210, 74, 0.46), rgba(200, 242, 79, 0.16));
    transform: skewX(-10deg);
    opacity: 0.72;
  }
}

.discounted-cases__headline-shadow {
  top: 50%;
  left: 47%;
  right: auto;
  bottom: auto;
  color: rgba(200, 242, 79, 0.07);
  font-size: clamp(10rem, 22vw, 22rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  text-transform: uppercase;
  transform: translate(-38%, -52%) rotate(-7deg);
  white-space: nowrap;
}

.discounted-cases__streaks {
  background:
    linear-gradient(
      118deg,
      transparent 0 18%,
      rgba(255, 255, 255, 0.22) 18.2% 18.6%,
      transparent 19%
    ),
    linear-gradient(
      118deg,
      transparent 0 36%,
      rgba(200, 242, 79, 0.36) 36.1% 36.5%,
      transparent 37%
    ),
    linear-gradient(
      118deg,
      transparent 0 63%,
      rgba(244, 210, 74, 0.3) 63.1% 63.55%,
      transparent 64%
    ),
    linear-gradient(
      118deg,
      transparent 0 78%,
      rgba(75, 104, 253, 0.22) 78.1% 78.45%,
      transparent 79%
    );
  opacity: 0.8;
}

.discounted-cases__grid {
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(0deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 62px 62px;
  mask-image: linear-gradient(90deg, transparent, black 18%, black 88%, transparent);
  opacity: 0.56;
}

.discounted-cases__glow {
  background:
    radial-gradient(ellipse at 58% 47%, rgba(200, 242, 79, 0.42), transparent 28%),
    radial-gradient(ellipse at 76% 58%, rgba(75, 104, 253, 0.28), transparent 26%),
    radial-gradient(ellipse at 24% 18%, rgba(244, 210, 74, 0.3), transparent 22%);
  mix-blend-mode: screen;
}

.discounted-cases__noise {
  background-image:
    radial-gradient(rgba(255, 255, 255, 0.24) 1px, transparent 1px),
    linear-gradient(90deg, transparent 0 8px, rgba(255, 255, 255, 0.035) 8px 9px, transparent 9px);
  background-size:
    26px 26px,
    18px 100%;
  opacity: 0.28;
}

.discounted-cases__header {
  position: relative;
  max-width: 330px;
  z-index: 1;

  h2 {
    margin: 0.35rem 0 0;
    color: #ffffff;
    font-size: clamp(2.5rem, 4.8vw, 5rem);
    line-height: 0.92;
    text-transform: uppercase;
    letter-spacing: 0;
    text-shadow: 0 6px 26px rgba(0, 0, 0, 0.45);
  }

  .discounted-cases__description {
    margin: 0.85rem 0 0;
    color: rgba(255, 255, 255, 0.76);
    font-size: 1rem;
    line-height: 1.5;
  }

  @include tablet {
    max-width: 640px;
    text-align: center;
    justify-self: center;
  }

  @include mobile {
    text-align: left;
  }
}

.discounted-cases__proof {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-xs;
  margin-top: $space-lg;
}

.discounted-cases__proof-item {
  display: flex;
  flex-direction: column;
  gap: $space-2xs;
  padding: $space-sm;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: $radius-sm;
  background: rgba(3, 11, 19, 0.62);
  backdrop-filter: blur(8px);

  strong {
    color: $text-light-200;
    font-size: 1.5rem;
    line-height: 1;
  }

  span {
    color: $text-dim;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &--edge {
    border-color: rgba(200, 242, 79, 0.42);

    strong {
      color: $accent-lime;
      text-shadow: 0 0 20px rgba(200, 242, 79, 0.35);
    }
  }
}

.discounted-cases__eyebrow {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0.35rem 0.65rem;
  margin: 0 0 1.45rem;
  border-radius: $radius-sm;
  background: rgba(200, 242, 79, 0.13);
  border: 1px solid rgba(200, 242, 79, 0.35);
  color: $accent-lime;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.discounted-cases__list {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.1rem;
  align-items: center;

  :deep(.singleCase-wrapper--image-container) {
    width: 100%;
    height: auto;
    aspect-ratio: 214 / 297;
  }

  @media (max-width: 1536px) {
    gap: 0.9rem;
  }

  @include tablet {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }

  @include mobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
  }
}

.discounted-cases__link {
  position: relative;
  display: block;
  min-width: 0;
  text-decoration: none;
}

@media (prefers-reduced-motion: reduce) {
  .discounted-cases::after {
    animation: none;
    opacity: 0.35;
  }
}

@keyframes discounted-cases-sweep {
  0%,
  48% {
    transform: translateX(-62%) skewX(-14deg);
    opacity: 0;
  }

  58% {
    opacity: 0.85;
  }

  78%,
  100% {
    transform: translateX(62%) skewX(-14deg);
    opacity: 0;
  }
}
</style>
